import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
    Container, Grid, Header, Loader, Dimmer, Button, Message, Segment, Label, Icon,
} from 'semantic-ui-react';
import moment from 'moment';
import { getConferenceDetail } from './lib/tickets';
import { useAuthStore } from './lib/authStore';
import { buildPath } from './lib/tenant';
import { t } from './locales';
import { formatMoney } from './lib/currency';

/** Re-renders every 30s so "LIVE" flags flip on/off as the conference day progresses. */
function useNow(intervalMs = 30000) {
    const [now, setNow] = useState(() => moment());
    useEffect(() => {
        const id = setInterval(() => setNow(moment()), intervalMs);
        return () => clearInterval(id);
    }, [intervalMs]);
    return now;
}

function isSessionLive(session, now) {
    if (!session.start_time || !session.effective_duration_minutes) return false;
    const start = moment(session.start_time);
    const end = start.clone().add(session.effective_duration_minutes, 'minutes');
    return now.isBetween(start, end, null, '[)');
}

function SessionRow({ session, roomName, now, onOpenEvent }) {
    const isPlaceholder = session.is_placeholder;
    const isLive = isSessionLive(session, now);
    return (
        <div
            style={{
                display: 'flex', alignItems: 'center', gap: '0.75rem',
                padding: '0.6rem 0.9rem', borderRadius: 8,
                background: isPlaceholder ? '#f5f5f5' : '#fff',
                border: isLive ? '1px solid #e0245e' : '1px solid #ece8f5',
                marginBottom: '0.5rem',
                cursor: isPlaceholder ? 'default' : 'pointer',
            }}
            onClick={() => !isPlaceholder && onOpenEvent(session)}
        >
            <div style={{ minWidth: 64, fontWeight: 600, color: 'var(--colour_menu_main)' }}>
                {session.start_time ? moment(session.start_time).format('HH:mm') : '—'}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontWeight: isPlaceholder ? 400 : 600, color: isPlaceholder ? '#888' : '#333' }}>
                    {session.title}
                </p>
                {roomName && <p style={{ margin: 0, fontSize: '0.8rem', color: '#999' }}>{roomName}</p>}
            </div>
            {isLive && (
                <Label size="mini" style={{ backgroundColor: '#e0245e', color: '#fff', fontWeight: 700 }}>
                    <Icon name="circle" style={{ marginRight: 4 }} />
                    {t('conferenceDetail.live')}
                </Label>
            )}
            {!isPlaceholder && <Icon name="chevron right" style={{ color: '#bbb' }} />}
        </div>
    );
}

function ConferenceDetail() {
    const { conferenceId } = useParams();
    const navigate = useNavigate();
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
    const now = useNow();

    const { data: conference, isLoading, isError } = useQuery({
        queryKey: ['ticketing-conference', conferenceId],
        queryFn: () => getConferenceDetail(conferenceId),
    });

    const tracksById = useMemo(() => {
        const map = new Map();
        (conference?.tracks ?? []).forEach(t => map.set(t.id, t));
        return map;
    }, [conference]);

    const roomsById = useMemo(() => {
        const map = new Map();
        (conference?.rooms ?? []).forEach(r => map.set(r.id, r));
        return map;
    }, [conference]);

    // Group sessions by track (falling back to "General Schedule" when no track set)
    const sessionsByTrack = useMemo(() => {
        const groups = new Map();
        (conference?.sessions ?? []).forEach(s => {
            const key = s.track ?? 'none';
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key).push(s);
        });
        return groups;
    }, [conference]);

    if (isLoading) {
        return (
            <Dimmer active inverted page>
                <Loader size="large" content={t('conferenceDetail.loading')} />
            </Dimmer>
        );
    }

    if (isError || !conference || conference.error) {
        return (
            <Container className="contentContainer">
                <Message negative>
                    <Message.Header>{t('conferenceDetail.notFoundTitle')}</Message.Header>
                    <p>{t('conferenceDetail.notFoundBody')}</p>
                </Message>
                <Button onClick={() => navigate(buildPath('/conferences'))}>{t('conferenceDetail.backToConferences')}</Button>
            </Container>
        );
    }

    const isSoldOut = conference.tickets_remaining === 0;

    const handleGetPass = () => {
        const checkoutParams = `?conferenceId=${conferenceId}`;
        if (!isAuthenticated) {
            navigate(buildPath(`/register?next=${encodeURIComponent(buildPath('/checkout') + checkoutParams)}`));
        } else {
            navigate(buildPath(`/checkout${checkoutParams}`));
        }
    };

    const openEvent = (session) => {
        if (!session.event_id) return;
        navigate(buildPath(`/events/${session.event_id}?instanceId=${session.event_instance_id}`));
    };

    return (
        <Container className="contentContainer">
            <Button
                basic
                icon="arrow left"
                content={t('conferenceDetail.allConferences')}
                onClick={() => navigate(buildPath('/conferences'))}
                style={{ marginBottom: '1.5rem' }}
            />

            <Grid stackable>
                <Grid.Column width={10}>
                    <div style={{
                        height: 220, borderRadius: 12, marginBottom: '1.5rem',
                        background: 'linear-gradient(135deg, var(--colour_menu_main), var(--colour_menu_light))',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '5rem' }}>🎪</span>
                    </div>

                    <Header as="h1" className="sectionHeader">{conference.name}</Header>

                    {conference.start_date && (
                        <p style={{ color: '#666', marginBottom: '0.5rem' }}>
                            <Icon name="calendar alternate outline" />
                            {moment(conference.start_date).format('dddd D MMMM YYYY')}
                            {conference.end_date && conference.end_date !== conference.start_date
                                ? ` – ${moment(conference.end_date).format('dddd D MMMM YYYY')}` : ''}
                        </p>
                    )}
                    {conference.location && (
                        <p style={{ color: '#666', marginBottom: '0.5rem' }}>
                            <Icon name="map marker alternate" />
                            {conference.location}
                        </p>
                    )}

                    <p style={{ lineHeight: 1.7, whiteSpace: 'pre-wrap', marginTop: '1rem' }}>
                        {conference.description}
                    </p>

                    {/* Schedule */}
                    {conference.sessions?.length > 0 && (
                        <div style={{ marginTop: '2.5rem' }}>
                            <Header as="h3" style={{ color: 'var(--colour_menu_main)', marginBottom: '1rem' }}>
                                <Icon name="calendar outline" />
                                {t('conferenceDetail.scheduleTitle')}
                            </Header>
                            {Array.from(sessionsByTrack.entries()).map(([trackId, sessions]) => (
                                <Segment key={trackId} style={{ borderRadius: 12, marginBottom: '1rem' }}>
                                    <p style={{ fontWeight: 700, color: 'var(--colour_menu_main)', marginBottom: '0.75rem' }}>
                                        {trackId !== 'none' ? tracksById.get(trackId)?.name : t('conferenceDetail.generalSchedule')}
                                    </p>
                                    {sessions
                                        .slice()
                                        .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''))
                                        .map(s => (
                                            <SessionRow
                                                key={s.id}
                                                session={s}
                                                roomName={s.room ? roomsById.get(s.room)?.name : null}
                                                now={now}
                                                onOpenEvent={openEvent}
                                            />
                                        ))}
                                </Segment>
                            ))}
                        </div>
                    )}
                </Grid.Column>

                <Grid.Column width={6}>
                    <Segment raised style={{ borderRadius: 12, padding: '2rem' }}>
                        <Header as="h3" style={{ color: 'var(--colour_menu_main)', marginBottom: '1.5rem' }}>
                            {t('conferenceDetail.getPass')}
                        </Header>

                        <Label
                            size="large"
                            style={{ backgroundColor: 'var(--colour_heading_success)', color: '#fff', marginBottom: '1.5rem', display: 'inline-block' }}
                        >
                            {t('conferenceDetail.passPrice', { price: formatMoney(conference.display_price ?? conference.ticket_price) })}
                        </Label>

                        <p style={{ color: '#555', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
                            {t('conferenceDetail.passIncludes')}
                        </p>

                        {conference.tickets_remaining !== null && conference.tickets_remaining <= 10 && !isSoldOut && (
                            <p style={{ color: '#e67e22', fontWeight: 600, marginBottom: '1rem' }}>
                                <Icon name="ticket" />
                                {t('conferenceDetail.onlyXLeft', { count: conference.tickets_remaining })}
                            </p>
                        )}

                        <Button
                            fluid
                            size="large"
                            disabled={isSoldOut}
                            onClick={handleGetPass}
                            style={{
                                backgroundColor: isSoldOut ? '#ccc' : 'var(--colour_menu_main)',
                                color: '#fff',
                                borderRadius: 8,
                            }}
                        >
                            {isSoldOut ? t('conferenceDetail.soldOutButton') : t('conferenceDetail.continueToCheckout')}
                        </Button>

                        {!isAuthenticated && !isSoldOut && (
                            <p style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: '#888', textAlign: 'center' }}>
                                {t('conferenceDetail.signInPrompt')}
                            </p>
                        )}
                    </Segment>
                </Grid.Column>
            </Grid>
        </Container>
    );
}

export default ConferenceDetail;
